import * as React from "react";
import { Icon, IconInput, Spinner } from "./Icon";

/**
 * Ported from ScadsysIdentity's components/Modal.tsx Button, with two changes: every class map is
 * expressed in semantic tokens so both themes work, and `loading` is first-class because almost
 * every call site in that app hand-rolled a disabled+spinner combination.
 */

export type ButtonVariant =
    | "primary"
    | "secondary"
    | "danger"
    | "success"
    | "warning"
    | "info"
    | "outline-primary"
    | "outline-secondary"
    | "outline-danger"
    | "outline-success"
    | "ghost";

export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    fullWidth?: boolean;
    /** Shows a spinner in place of `icon` and disables the button. */
    loading?: boolean;
    icon?: IconInput;
    children?: React.ReactNode;
}

const BASE =
    "font-medium rounded-btn transition-[color,background-color,border-color,transform] duration-150 ease-out " +
    "inline-flex items-center justify-center gap-2 active:scale-[0.97] " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface";

const SIZES: Record<ButtonSize, string> = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base",
};

const VARIANTS: Record<ButtonVariant, string> = {
    primary: "bg-primary text-on-primary hover:bg-primary-hover",
    secondary: "bg-surface-muted text-text border-[1.5px] border-border hover:bg-surface-hover",
    danger: "bg-danger text-white hover:opacity-90",
    success: "bg-success text-white hover:opacity-90",
    // Gold reads too light for white text at AA contrast — Bloom's own accent button uses
    // near-black text on gold for the same reason.
    warning: "bg-warning text-gold-900 hover:opacity-90",
    info: "bg-info text-white hover:opacity-90",
    "outline-primary": "border-[1.5px] border-primary text-primary-text hover:bg-primary-soft",
    "outline-secondary": "border-[1.5px] border-border-strong text-text bg-surface hover:bg-surface-hover",
    "outline-danger": "border-[1.5px] border-danger text-danger-text hover:bg-danger-soft",
    "outline-success": "border-[1.5px] border-success text-success-text hover:bg-success-soft",
    ghost: "text-text-weak hover:bg-surface-hover hover:text-text",
};

export const Button: React.FC<ButtonProps> = ({
    variant = "primary",
    size = "md",
    fullWidth = false,
    loading = false,
    icon,
    className = "",
    disabled,
    children,
    type = "button",
    ...props
}) => {
    const isDisabled = disabled || loading;

    return (
        <button
            type={type}
            className={[
                BASE,
                SIZES[size],
                VARIANTS[variant],
                isDisabled ? "opacity-50 cursor-not-allowed" : "",
                fullWidth ? "w-full" : "",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
            disabled={isDisabled}
            aria-busy={loading || undefined}
            {...props}
        >
            {loading ? <Spinner /> : icon ? <Icon icon={icon} /> : null}
            {children}
        </button>
    );
};

/**
 * The icon-only action used in table action columns. It carries its own accessible name because a
 * bare icon has none — ScadsysIdentity's equivalents render as an unlabelled button to a screen reader.
 */
export interface IconButtonProps extends Omit<ButtonProps, "children" | "icon"> {
    icon: IconInput;
    label: string;
}

export const IconButton: React.FC<IconButtonProps> = ({ icon, label, size = "sm", variant = "outline-secondary", ...props }) => (
    <Button variant={variant} size={size} title={label} aria-label={label} icon={icon} {...props} />
);

export default Button;
